import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Collapse,
  InputBase,
  Paper,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import {
  ExpandLess,
  ExpandMore,
  Search,
  FiberManualRecord,
  ChevronRight,
} from "@material-ui/icons";
import { useHelpCategories } from "../../hooks/useHelp";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    padding: theme.spacing(3, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    marginTop: theme.spacing(2),
  },
  searchInput: {
    flex: 1,
    fontSize: "0.875rem",
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    fontSize: "1.25rem",
  },
  categoriesWrapper: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  categoryItem: {
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  categoryItemActive: {
    backgroundColor: theme.palette.action.selected,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  categoryText: {
    "& .MuiListItemText-primary": {
      fontSize: "0.875rem",
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
  },
  subcategoryItem: {
    paddingLeft: theme.spacing(4),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(0.25),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  subcategoryText: {
    "& .MuiListItemText-primary": {
      fontSize: "0.8125rem",
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
  },
  articleItem: {
    paddingLeft: theme.spacing(6),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(0.25),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  articleText: {
    "& .MuiListItemText-primary": {
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
  },
  badge: {
    height: 18,
    minWidth: 18,
    fontSize: "0.625rem",
    fontWeight: 600,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  expandIcon: {
    minWidth: 24,
    color: theme.palette.text.secondary,
    transition: "transform 0.2s ease",
  },
  expandIconRotated: {
    transform: "rotate(90deg)",
  },
  dotIcon: {
    fontSize: "0.375rem",
    color: theme.palette.text.disabled,
  },
  chevronIcon: {
    fontSize: "0.875rem",
    color: theme.palette.text.disabled,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  emptyState: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
}));

const HelpSidebar = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [expanded, setExpanded] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const { categories, loading } = useHelpCategories({
    searchParam: "",
    parentId: null,
  });

  // Auto-expandir categoria ativa
  useEffect(() => {
    if (categories.length > 0 && location.pathname.includes("/help/")) {
      const newExpanded = { ...expanded };
      categories.forEach((category) => {
        if (location.pathname.includes(category.slug)) {
          newExpanded[category.id] = true;
        }
        if (category.subcategories) {
          category.subcategories.forEach((sub) => {
            if (location.pathname.includes(sub.slug)) {
              newExpanded[category.id] = true;
            }
          });
        }
      });
      setExpanded(newExpanded);
    }
  }, [categories, location.pathname]);

  const handleToggle = (categoryId) => {
    setExpanded((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleCategoryClick = (category) => {
    history.push(`/help/${category.slug}`);
    handleToggle(category.id);
  };

  const handleArticleClick = (article) => {
    history.push(`/help/article/${article.slug}`);
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      history.push(`/help/search?q=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const getCurrentLang = () => {
    const lang = i18n.language || "pt";
    return lang.toLowerCase().substring(0, 2);
  };

  const getCategoryName = (category) => {
    const lang = getCurrentLang();
    if (lang === "en") return category.nameEn;
    if (lang === "es") return category.nameEs;
    return category.namePt;
  };

  const getArticleTitle = (article) => {
    const lang = getCurrentLang();
    if (lang === "en") return article.titleEn;
    if (lang === "es") return article.titleEs;
    return article.titlePt;
  };

  const getTotalArticles = (category) => {
    let total = category.articles?.length || 0;
    if (category.subcategories) {
      category.subcategories.forEach((sub) => {
        total += sub.articles?.length || 0;
      });
    }
    return total;
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography className={classes.title}>
          📚 {i18n.t("helpCenter.title")}
        </Typography>
        <Typography className={classes.subtitle}>
          {i18n.t("helpCenter.sidebarSubtitle") || "Encontre respostas para suas dúvidas"}
        </Typography>

        {/* Search Box */}
        <Box className={classes.searchBox}>
          <Search className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            placeholder={i18n.t("helpCenter.search") || "Buscar artigos..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </Box>
      </Box>

      {/* Categories List */}
      <Box className={classes.categoriesWrapper}>
        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress size={32} />
          </Box>
        ) : categories.length === 0 ? (
          <Box className={classes.emptyState}>
            {i18n.t("helpCenter.noCategories") || "Nenhuma categoria disponível"}
          </Box>
        ) : (
          <List component="nav" disablePadding>
            {categories.map((category) => {
              const totalArticles = getTotalArticles(category);
              const isExpanded = expanded[category.id];
              const isActive = location.pathname.includes(category.slug);

              return (
                <React.Fragment key={category.id}>
                  {/* Categoria Principal */}
                  <ListItem
                    button
                    className={`${classes.categoryItem} ${
                      isActive ? classes.categoryItemActive : ""
                    }`}
                    onClick={() => handleCategoryClick(category)}
                  >
                    <Box className={classes.expandIcon}>
                      {category.subcategories && category.subcategories.length > 0 ? (
                        <ChevronRight
                          className={isExpanded ? classes.expandIconRotated : ""}
                        />
                      ) : null}
                    </Box>

                    <ListItemText
                      className={classes.categoryText}
                      primary={
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <span>{getCategoryName(category)}</span>
                          {totalArticles > 0 && (
                            <Chip
                              label={totalArticles}
                              size="small"
                              className={classes.badge}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>

                  {/* Subcategorias */}
                  {category.subcategories && category.subcategories.length > 0 && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {category.subcategories.map((subcategory) => (
                          <React.Fragment key={subcategory.id}>
                            <ListItem
                              button
                              className={classes.subcategoryItem}
                              onClick={() => handleCategoryClick(subcategory)}
                            >
                              <ListItemText
                                className={classes.subcategoryText}
                                primary={
                                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                      <FiberManualRecord className={classes.dotIcon} />
                                      <span>{getCategoryName(subcategory)}</span>
                                    </Box>
                                    {subcategory.articles && subcategory.articles.length > 0 && (
                                      <Chip
                                        label={subcategory.articles.length}
                                        size="small"
                                        className={classes.badge}
                                      />
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>

                            {/* Artigos da Subcategoria */}
                            {subcategory.articles && subcategory.articles.length > 0 && (
                              <List component="div" disablePadding>
                                {subcategory.articles.slice(0, 5).map((article) => (
                                  <ListItem
                                    button
                                    key={article.id}
                                    className={classes.articleItem}
                                    onClick={() => handleArticleClick(article)}
                                  >
                                    <ListItemText
                                      className={classes.articleText}
                                      primary={
                                        <Box display="flex" alignItems="center">
                                          <ChevronRight className={classes.chevronIcon} />
                                          <span>{getArticleTitle(article)}</span>
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </React.Fragment>
                        ))}
                      </List>
                    </Collapse>
                  )}

                  {/* Artigos da Categoria Principal */}
                  {category.articles && category.articles.length > 0 && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {category.articles.slice(0, 5).map((article) => (
                          <ListItem
                            button
                            key={article.id}
                            className={classes.subcategoryItem}
                            onClick={() => handleArticleClick(article)}
                          >
                            <ListItemText
                              className={classes.subcategoryText}
                              primary={
                                <Box display="flex" alignItems="center">
                                  <ChevronRight className={classes.chevronIcon} />
                                  <span>{getArticleTitle(article)}</span>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default HelpSidebar;
